import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Moon, 
  Sun, 
  User, 
  LogOut, 
  Package,
  Calendar,
  TrendingUp,
  ChevronRight,
  Award
} from 'lucide-react';
import { useSession } from '../../hooks/useSession';
import { useTheme } from '../../hooks/useTheme';
import './MainLayout.css';

// Accept isScrolled as a prop from MainLayout
const TopNav = ({ isScrolled = false }) => {
  const { session, clearSession } = useSession();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Remove the local state management for isScrolled
  // const [isScrolled, setIsScrolled] = useState(false); - REMOVE THIS
  // const setIsScrolled = false; - REMOVE THIS

  // Remove the scroll listener useEffect - it's now handled in MainLayout
  // useEffect(() => {
  //   const handleScroll = () => {
  //     setIsScrolled(window.scrollY > 20);
  //   };
  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    clearSession();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const navItems = [
    { path: '/', icon: TrendingUp, label: 'Live Rates' },
    { path: '/products', icon: Package, label: 'Products' },
    { path: '/booking', icon: Calendar, label: 'Book Now' },
  ];

  const menuVariants = {
    closed: {
      x: '100%',
      opacity: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    },
    open: {
      x: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 30 }
    }
  };

  const backdropVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 }
  };

  const itemVariants = {
    closed: { x: 20, opacity: 0 },
    open: (i) => ({
      x: 0,
      opacity: 1,
      transition: { delay: i * 0.08, type: 'spring', stiffness: 300, damping: 30 }
    })
  };

  return (
    <>
      <motion.nav 
        className={`top-nav ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className="container-custom">
          <div className="top-nav-inner">
            {/* Brand Logo */}
            <Link to="/" className="nav-brand">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="brand-wrapper"
              >
                <span className="brand-icon">🌟</span>
                <span className="brand-name">Tatvam Silverr</span>
                <motion.span 
                  className="brand-badge"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Award size={12} />
                </motion.span>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="nav-links desktop-nav">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-link ${isActive ? 'active' : ''}`}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeNavIndicator"
                        className="nav-link-indicator"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}

              <div className="nav-divider" />

              <motion.button 
                onClick={toggleTheme} 
                className="theme-toggle"
                whileHover={{ scale: 1.1, rotate: 20 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Toggle theme"
              >
                <motion.div
                  initial={false}
                  animate={{ rotate: theme === 'dark' ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                </motion.div>
              </motion.button>

              {session ? (
                <div className="nav-user-section">
                  <Link to="/profile" className="nav-user-btn">
                    <motion.div 
                      className="user-avatar"
                      whileHover={{ scale: 1.05 }}
                      style={{
                        background: theme === 'dark' 
                          ? 'rgba(255, 255, 255, 0.15)' 
                          : 'rgba(255, 255, 255, 0.25)'
                      }}
                    >
                      <User size={18} />
                    </motion.div>
                    <span className="user-name">
                      {session.user?.name || 'User'}
                    </span>
                  </Link>
                  <motion.button 
                    onClick={handleLogout} 
                    className="btn-logout"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </motion.button>
                </div>
              ) : (
                <div className="nav-auth-section">
                  <Link to="/login" className="nav-link login-link">
                    Login
                  </Link>
                  <Link to="/register" className="btn-primary">
                    <span>Sign Up</span>
                    <ChevronRight size={16} />
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <motion.button 
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              variants={backdropVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="menu-overlay"
              onClick={() => setIsMenuOpen(false)}
              style={{
                background: theme === 'dark' 
                  ? 'rgba(0, 0, 0, 0.7)' 
                  : 'rgba(0, 0, 0, 0.5)'
              }}
            />
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="mobile-menu-panel"
              data-theme={theme}
            >
              <div className="mobile-menu-header">
                <div className="menu-brand">
                  <span className="brand-icon">🌟</span>
                  <span className="brand-name">Tatvam Silverr</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsMenuOpen(false)}
                  className="menu-close-btn"
                >
                  <X size={24} />
                </motion.button>
              </div>

              <div className="mobile-menu-content">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.path}
                      custom={index}
                      variants={itemVariants}
                      initial="closed"
                      animate="open"
                    >
                      <Link
                        to={item.path}
                        className={`mobile-nav-link ${isActive ? 'active' : ''}`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="mobile-nav-link-content">
                          <Icon size={20} className="mobile-nav-icon" />
                          <span>{item.label}</span>
                        </div>
                        {isActive && (
                          <motion.div
                            layoutId="activeMobileIndicator"
                            className="mobile-nav-active-indicator"
                          >
                            <ChevronRight size={20} />
                          </motion.div>
                        )}
                      </Link>
                    </motion.div>
                  );
                })}

                <motion.div 
                  className="mobile-menu-divider"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.3 }}
                />

                <motion.div
                  variants={itemVariants}
                  custom={3}
                  initial="closed"
                  animate="open"
                  className="mobile-menu-actions"
                >
                  <button 
                    onClick={toggleTheme} 
                    className="mobile-theme-toggle"
                  >
                    {theme === 'light' ? (
                      <>
                        <Moon size={20} />
                        <span>Dark Mode</span>
                      </>
                    ) : (
                      <>
                        <Sun size={20} />
                        <span>Light Mode</span>
                      </>
                    )}
                    <motion.span 
                      className="theme-toggle-badge"
                      initial={false}
                      animate={{
                        backgroundColor: theme === 'dark' ? '#8b5cf6' : '#f59e0b'
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {theme === 'light' ? '🌙' : '☀️'}
                    </motion.span>
                  </button>
                </motion.div>

                <motion.div 
                  className="mobile-menu-divider"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.4 }}
                />

                <motion.div
                  variants={itemVariants}
                  custom={4}
                  initial="closed"
                  animate="open"
                  className="mobile-menu-auth"
                >
                  {session ? (
                    <>
                      <div className="mobile-user-info">
                        <motion.div 
                          className="mobile-user-avatar"
                          whileHover={{ scale: 1.05 }}
                          style={{
                            background: theme === 'dark' 
                              ? 'var(--gradient-primary)' 
                              : 'var(--gradient-primary)'
                          }}
                        >
                          <User size={24} />
                        </motion.div>
                        <div>
                          <div className="mobile-user-name">
                            {session.user?.name || 'User'}
                          </div>
                          <div className="mobile-user-email">
                            {session.user?.email || 'user@email.com'}
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        className="mobile-nav-link"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="mobile-nav-link-content">
                          <User size={20} className="mobile-nav-icon" />
                          <span>Profile</span>
                        </div>
                      </Link>
                      <button 
                        onClick={handleLogout} 
                        className="mobile-logout-btn"
                      >
                        <LogOut size={20} />
                        <span>Logout</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="mobile-nav-link"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="mobile-nav-link-content">
                          <User size={20} className="mobile-nav-icon" />
                          <span>Login</span>
                        </div>
                      </Link>
                      <Link
                        to="/register"
                        className="mobile-signup-btn"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span>Create Account</span>
                        <ChevronRight size={20} />
                      </Link>
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default TopNav;